/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import {
  Button,
  Select,
  FormControl,
  TextField,
  MenuItem,
  InputLabel as Label,
} from '@mui/material'

import {
  addDotInAmount,
  PURPOSE,
  PURPOSE_OPTIONS,
  DEFAULT_SELECTION_VALUES,
  TERM_OPTIONS,
  LABEL,
  MAX_CREDIT_AMOUNT,
  MIN_CREDIT_AMOUNT,
  NUM_OF_VISIBLE_OPTIONS,
  removeDotInAmount,
  getAmountOptions,
} from '../../utils/loanSelector.utils'
import { trackCTAClick } from '../../utils/segment'
import {
  InputNames,
  useLoanSelection,
} from '../../contexts/LoanSelectorContext'
import { removeStorage } from '../../utils/storage'
import { EntryPoint } from '../../utils/entryPoint.enum'
import { getRegRoutePath } from '../../utils/url'
import { isSmava } from '../../utils/getTenantInfo.utils'

const LoanSelector = () => {
  const router = useRouter()
  const minAmount = router?.query?.minAmount as unknown as number
  const maxAmount = router?.query?.maxAmount as unknown as number

  const { loanSelection: savedSelection, setLoanSelection: saveSelection } =
    useLoanSelection()
  const [loanSelection, setLoanSelection] = useState(savedSelection)
  const [initialPaymentHelperText, setInitialPaymentHelperText] =
    useState<string>()

  const isPurposeCar =
    loanSelection.purpose.toString() === PURPOSE.CAR.toString()

  const numberInputs = [InputNames.VehiclePrice, InputNames.InitialPayment]

  const handleChange = (selectName: InputNames) => async (event: any) => {
    const { value }: { value: string } = event.target
    let newValue: number | string = value

    if (newValue && numberInputs.includes(selectName)) {
      const amount = removeDotInAmount(value)
      const isAmountANumber =
        typeof amount === 'number' && !Number.isNaN(amount)
      newValue = isAmountANumber ? amount : DEFAULT_SELECTION_VALUES[selectName]
    }

    setLoanSelection({
      ...loanSelection,
      [selectName]: newValue,
    })
    if (!numberInputs.includes(selectName)) {
      saveSelection({
        ...loanSelection,
        [selectName]: newValue,
      })
    }
  }

  const validate = (selectName: InputNames, value: string | number) => {
    if (selectName === InputNames.VehiclePrice) {
      const isValid = parseInt(value.toString(), 10) >= MIN_CREDIT_AMOUNT
      if (!isValid) {
        const newSelection = {
          ...loanSelection,
          [InputNames.VehiclePrice]: MIN_CREDIT_AMOUNT,
          [InputNames.InitialPayment]: 0,
        }
        setLoanSelection(newSelection)
        saveSelection(newSelection)
      } else {
        // revalidate inital payment
        validate(InputNames.InitialPayment, loanSelection.initialPayment)
      }
    }
    if (selectName === InputNames.InitialPayment) {
      const isValid =
        loanSelection.vehiclePrice - parseInt(value.toString(), 10) >=
        MIN_CREDIT_AMOUNT
      if (!isValid) {
        let newInitialPayment = loanSelection.vehiclePrice - MIN_CREDIT_AMOUNT
        if (newInitialPayment < 0)
          newInitialPayment = DEFAULT_SELECTION_VALUES[selectName]
        const newLoanSelectionValues = {
          ...loanSelection,
          [selectName]: newInitialPayment,
          [InputNames.Amount]: MIN_CREDIT_AMOUNT,
        }
        setLoanSelection(newLoanSelectionValues)
        saveSelection(newLoanSelectionValues)
        setInitialPaymentHelperText(
          `Bei einem Kaufpreis von ${addDotInAmount(
            loanSelection.vehiclePrice,
          )} € darf die Anzahlung höchstens ${addDotInAmount(
            loanSelection.vehiclePrice - MIN_CREDIT_AMOUNT,
          )} € betragen.`,
        )
      } else {
        setInitialPaymentHelperText(undefined)
      }
    }
  }

  const handleBlur = (selectName: InputNames) => async (event: any) => {
    const { value }: { value: string } = event.target
    if (numberInputs.includes(selectName)) {
      if (!value) {
        setLoanSelection({
          ...loanSelection,
          [selectName]: DEFAULT_SELECTION_VALUES[selectName],
        })
        saveSelection({
          ...loanSelection,
          [selectName]: DEFAULT_SELECTION_VALUES[selectName],
        })
      } else {
        loanSelection.amount =
          loanSelection.vehiclePrice - loanSelection.initialPayment

        saveSelection(loanSelection)
        validate(selectName, removeDotInAmount(value))
      }
    }
  }

  const handleFocus = (event: any) => event.target.select()

  const handleCategoryChange = (selectName: string) => async (event: any) => {
    const newPurpose = event.target?.value

    if (newPurpose) {
      const newAmount = loanSelection.amount
      const newTerm = loanSelection.term

      const newState = {
        ...loanSelection,
        term: newTerm,
        amount: newAmount,
        [selectName]: newPurpose,
      }
      setLoanSelection(newState)
      saveSelection(newState)
    }
  }

  const handleSubmit = (formValues: any) => {
    const { pathname, query } = router

    // eslint-disable-next-line no-undef
    const pageUrl = window?.location?.href

    trackCTAClick({ ...formValues, pageUrl })

    router.push({
      pathname: getRegRoutePath(pathname),
      query: {
        ...query,
        ...formValues,
        backUrl: pageUrl,
        entryPoint: EntryPoint.BaseReceived,
      },
    })
  }

  useEffect(() => {
    // team catapult needs a clean session storage
    removeStorage('sessionStorage', 'IMOD_CURRENT_NAVIGATION')
  }, [])

  return (
    <form action=''>
      <Label id={InputNames.Purpose}>{LABEL.Purpose}</Label>
      <FormControl variant='outlined' fullWidth size='small'>
        <Select
          fullWidth
          labelId={InputNames.Purpose}
          value={loanSelection[InputNames.Purpose]}
          onChange={handleCategoryChange(InputNames.Purpose)}
        >
          {PURPOSE_OPTIONS.map((purposeOption) => (
            <MenuItem key={purposeOption.value} value={purposeOption.value}>
              {purposeOption.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {isPurposeCar && isSmava ? (
        <>
          <Label id={InputNames.VehiclePrice}>{LABEL.VehiclePrice}</Label>
          <TextField
            type='tel'
            id={InputNames.VehiclePrice}
            fullWidth
            variant='outlined'
            size='small'
            value={addDotInAmount(loanSelection[InputNames.VehiclePrice])}
            onChange={handleChange(InputNames.VehiclePrice)}
            onBlur={handleBlur(InputNames.VehiclePrice)}
            onFocus={handleFocus}
            slotProps={{
              htmlInput: {
                min: 500,
                max: MAX_CREDIT_AMOUNT,
                pattern: 'd*',
                inputMode: 'decimal',
                style: { paddingBottom: '10px', paddingTop: '10px' },
              },
            }}
          />

          <Label id={InputNames.InitialPayment}>{LABEL.InitialPayment}</Label>
          <TextField
            type='tel'
            id={InputNames.InitialPayment}
            variant='outlined'
            size='small'
            fullWidth
            value={addDotInAmount(loanSelection[InputNames.InitialPayment])}
            onChange={handleChange(InputNames.InitialPayment)}
            onBlur={handleBlur(InputNames.InitialPayment)}
            onFocus={handleFocus}
            slotProps={{
              htmlInput: {
                pattern: 'd*',
                inputMode: 'decimal',
                style: { paddingBottom: '10px', paddingTop: '10px' },
              },
            }}
            helperText={initialPaymentHelperText}
          />
        </>
      ) : (
        <>
          <Label id={InputNames.Amount}>{LABEL.Amount}</Label>
          <FormControl variant='outlined' fullWidth size='small'>
            <Select
              fullWidth
              labelId={InputNames.Amount}
              data-testid={InputNames.Amount}
              value={loanSelection[InputNames.Amount]}
              onChange={handleChange(InputNames.Amount)}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 40 * NUM_OF_VISIBLE_OPTIONS,
                  },
                },
              }}
            >
              {getAmountOptions(minAmount, maxAmount).map((amountOption) => (
                <MenuItem key={amountOption} value={amountOption}>
                  {addDotInAmount(amountOption)} €
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}
      <Label id={InputNames.Term}>{LABEL.Term}</Label>
      <FormControl variant='outlined' fullWidth size='small'>
        <Select
          fullWidth
          labelId={InputNames.Term}
          data-testid={InputNames.Term}
          value={loanSelection[InputNames.Term]}
          onChange={handleChange(InputNames.Term)}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 40 * NUM_OF_VISIBLE_OPTIONS,
              },
            },
          }}
        >
          {TERM_OPTIONS.map((termOption) => (
            <MenuItem key={termOption} value={termOption}>
              {termOption} Monate
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant='contained'
        type='button'
        fullWidth
        size='large'
        onClick={() => {
          handleSubmit(loanSelection)
        }}
      >
        Jetzt Kreditvergleich starten
      </Button>
    </form>
  )
}

export default LoanSelector
